import Layout from 'layout/Layout'

import { FC, useCallback, useContext, useEffect, useRef } from 'react'
import { useIntl } from 'react-intl'
import { BrowserRouter, Route, Routes } from 'react-router-dom'
import { toast } from 'react-toastify'
import { VerificationPage } from 'ufinet-web-components'
import {
	AuthContext,
	authService,
	AuthStatus,
	IAuthUser,
	ITokenData,
	AuthorityPattern,
	UserTypesEnum,
} from 'ufinet-web-functions'
import { PrivateRoutes } from './protected/PrivateRoutes'

const PORTAL_API_BASE_URL = process.env.REACT_APP_PORTAL_API_BASE_URL || ''
const PORTAL_API_KEY = process.env.REACT_APP_PORTAL_API_KEY || ''

const extractTokenFromUrl = (): ITokenData | null => {
	const urlParams = new URLSearchParams(window.location.search)
	const accessToken = urlParams.get('token')
	const refreshToken = urlParams.get('refreshToken')
	if (accessToken) {
		return { accessToken, refreshToken: refreshToken || '' }
	}
	return null
}

const cleanTokenFromUrl = () => {
	const url = new URL(window.location.href)
	url.searchParams.delete('token')
	url.searchParams.delete('refreshToken')
	window.history.replaceState({}, '', url.toString())
}

const decodeJwtPayload = (token: string): Record<string, unknown> | null => {
	try {
		const base64Url = token.split('.')[1]
		const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
		const jsonPayload = decodeURIComponent(
			atob(base64)
				.split('')
				.map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
				.join('')
		)
		return JSON.parse(jsonPayload)
	} catch {
		return null
	}
}

const fetchAuthorities = async (accessToken: string): Promise<{ authorities: string[]; unauthorized: boolean }> => {
	try {
		const response = await fetch(`${PORTAL_API_BASE_URL}portalbackend/api/authorities`, {
			method: 'GET',
			headers: {
				Authorization: `Bearer ${accessToken}`,
				apiKey: PORTAL_API_KEY,
			},
		})
		if (response.status === 401 || response.status === 403) {
			return { authorities: [], unauthorized: true }
		}
		const data = await response.json()
		if (data?.authorities && Array.isArray(data.authorities) && data.authorities.length > 0) {
			return { authorities: data.authorities, unauthorized: false }
		}
	} catch (error) {
		console.warn('Failed to fetch authorities from API, falling back to JWT roles:', error)
	}
	return { authorities: [], unauthorized: false }
}

const saveAuthToLocalStorage = (tokenData: ITokenData, userData: IAuthUser) => {
	const token = {
		tokenType: 'Bearer',
		accessToken: tokenData.accessToken,
		refreshToken: tokenData.refreshToken,
		temporaryToken: null,
		mfaRequired: false,
	}
	localStorage.setItem('ufinet-auth', JSON.stringify({ status: 'LOGGED_IN', token, userData }))
}

const refreshAccessToken = async (): Promise<boolean> => {
	try {
		const raw = localStorage.getItem('ufinet-auth')
		if (!raw) return false

		const auth = JSON.parse(raw)
		const currentAccessToken = auth?.token?.accessToken
		const currentRefreshToken = auth?.token?.refreshToken
		if (!currentAccessToken || !currentRefreshToken) return false

		const response = await fetch(`${PORTAL_API_BASE_URL}portalbackend/api/auth/token/refresh`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${currentAccessToken}`,
				apiKey: PORTAL_API_KEY,
			},
			body: JSON.stringify({ refreshToken: currentRefreshToken }),
		})

		if (!response.ok) return false

		const data = await response.json()
		if (!data?.accessToken) return false

		// Update only the accessToken in localStorage, keep everything else
		auth.token.accessToken = data.accessToken
		localStorage.setItem('ufinet-auth', JSON.stringify(auth))

		return true
	} catch {
		return false
	}
}

const redirectToPortalLogin = () => {
	const portalLoginUrl = process.env.REACT_APP_URL_PORTAL_LOGIN
	if (!portalLoginUrl) {
		console.error('REACT_APP_URL_PORTAL_LOGIN is not configured')
		return
	}
	const currentUrl = window.location.href
	window.location.href = `${portalLoginUrl}?fromLegacyUrl=${encodeURIComponent(currentUrl)}`
}

const AppRoutes: FC = () => {
	const intl = useIntl()
	const { status, setStatus, token, setToken, clear } = useContext(AuthContext)
	const hasInitialized = useRef(false)
	const hasCompletedLogin = useRef(false)

	// On mount: extract token from URL or use existing, fetch authorities, and log in
	useEffect(() => {
		if (hasInitialized.current) return
		hasInitialized.current = true

		authService.setUpBroadcastChannel({ onLogoutReceived: clear })

		const urlToken = extractTokenFromUrl()
		if (urlToken) {
			cleanTokenFromUrl()
			setToken(urlToken)
		}

		// Fallback: read token from localStorage if context hasn't hydrated yet (e.g. page reload)
		let storedToken: ITokenData | null = null
		if (!urlToken && !token) {
			try {
				const raw = localStorage.getItem('ufinet-auth')
				if (raw) {
					const parsed = JSON.parse(raw)
					if (parsed?.token?.accessToken) {
						storedToken = parsed.token as ITokenData
					}
				}
			} catch {
				// ignore parse errors
			}
		}

		const tokenToUse = urlToken || token || storedToken
		if (!tokenToUse?.accessToken) {
			redirectToPortalLogin()
			return
		}

		const decoded = decodeJwtPayload(tokenToUse.accessToken)
		if (!decoded) {
			redirectToPortalLogin()
			return
		}

		// Async: fetch authorities from API, then complete login
		;(async () => {
			const result = await fetchAuthorities(tokenToUse.accessToken)

			if (result.unauthorized) {
				redirectToPortalLogin()
				return
			}

			let authorities = result.authorities as AuthorityPattern[]

			// Fallback: if API returned empty, parse from JWT roles
			if (authorities.length === 0 && typeof decoded.roles === 'string') {
				authorities = decoded.roles.split(',') as AuthorityPattern[]
			}

			const userData: IAuthUser = {
				valid: true,
				expired: false,
				username: (decoded.sub as string) || '',
				authorities,
				user: {
					fullName: (decoded.fullname as string) || '',
					type: ((decoded.usertype as string) || '') as UserTypesEnum,
				},
			}

			// Save complete auth object to localStorage
			saveAuthToLocalStorage(tokenToUse, userData)

			// Update React AuthContext
			setStatus(AuthStatus.LOGGED_IN, userData)
			hasCompletedLogin.current = true
		})()
	}, [])

	// Handle logout triggered by FetchAPI 401/403 or BroadcastChannel
	useEffect(() => {
		if (hasCompletedLogin.current && status === AuthStatus.LOGGED_OUT) {
			redirectToPortalLogin()
		}
	}, [status])

	// Periodically refresh token
	const refreshToken = useCallback(() => {
		if (status === AuthStatus.LOGGED_IN) {
			refreshAccessToken().then((success) => {
				if (!success) {
					toast.dismiss()
					toast.error(intl.formatMessage({ id: 'AUTH.SESSION.EXPIRED' }))
					redirectToPortalLogin()
				}
			})
		}
	}, [status, intl])

	useEffect(() => {
		const refreshInterval = setInterval(refreshToken, 15 * 60 * 1000)

		return () => {
			clearInterval(refreshInterval)
		}
	}, [refreshToken])

	return (
		<BrowserRouter>
			<Routes>
				<Route element={<Layout />}>
					{status === AuthStatus.LOGGED_IN ? (
						<>
							<Route path="/*" element={<PrivateRoutes />} />
						</>
					) : (
						<>
							<Route path="/*" element={<VerificationPage />} />
						</>
					)}
				</Route>
			</Routes>
		</BrowserRouter>
	)
}

export { AppRoutes }
